import { PlayerWrapper } from "@infinitaslearning/app-player";
import _ from "lodash";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingSpinner } from "components/LoadingSpinner";
import { useLanguage } from "hooks/useSelectedLanguage";

export interface PlayerProps {
  id: string;
  // biome-ignore lint/suspicious/noExplicitAny: PlayerWrapper accepts dynamic content structure
  data?: any;
  // biome-ignore lint/suspicious/noExplicitAny: PlayerWrapper accepts dynamic config structure
  config?: any;
  context?: "drilling" | "slimStampen";
}

export const Player: React.FC<PlayerProps> = ({ id, data, config = {}, context }) => {
  const { t } = useTranslation("common");
  const { currentLanguage } = useLanguage();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Translations must change if user switches language
  const defaultConfig = useMemo(
    () => ({
      translations: {
        submit: t("common:player.submit"),
        superSubmit: t("common:player.superSubmit"),
        view_full_screen: t("common:player.viewFullScreen"),
        dropdown_placeholder: t("common:player.dropdownPlaceholder"),
        required_answer: t("common:player.requiredAnswer"),
        retry: t("common:player.retry"),
        finish: t("common:player.finish"),
        feedback_bad: t("common:player.feedbackBad"),
        feedback_good: t("common:player.feedbackGood"),
        finished_already: t("common:player.finishedAlready"),
        back_to_exercise: t("common:player.backToExercise"),
        confirmation_text: t("common:player.confirmationText"),
        dropArea_dragHere: t("common:player.dropArea.dragHere"),
      },
    }),
    [currentLanguage],
  );

  /* biome-ignore lint/correctness/useExhaustiveDependencies:
      We check "config" dependency by value instead of reference to avoid entering infinite loop */
  const playerConfig = useMemo(
    () => _.merge({}, defaultConfig, config),
    [defaultConfig, JSON.stringify(config)],
  );

  // Player doesn't automatically re-render when data or config changes, so we need to
  // force a refresh by not rendering the "PlayerWrapper" instance for a moment
  const [forceRefresh, setForceRefresh] = useState(false);
  /* biome-ignore lint/correctness/useExhaustiveDependencies:
      Hook needs to run whenever config or data changes.
      We check "data" dependency by value instead of reference to avoid entering infinite loop. */
  useEffect(() => {
    setTimeout(() => {
      setForceRefresh(false);
    }, 50);

    // This callback function will be called anytime the hook is about to run, so we
    // use it to set the force flag to "true" to indicate the dependencies have changed
    return () => {
      setForceRefresh(true);
    };
  }, [id, context, JSON.stringify(data), playerConfig]);

  // If there's no data, no point in showing anything
  if (!data) {
    return null;
  }

  // Temporarily display loader while we force a reload of the Player instance
  if (forceRefresh) {
    return <LoadingSpinner />;
  }

  return <PlayerWrapper id={id} config={playerConfig} context={context} initialData={data} />;
};
